<!-- Component that is used to send messages to chat. Also controls video call -->
<template>
  <div class="mt2 mx2">
    <div class="phrase-holder" v-if="showPhrases">
      <PhraseList :chat="chat" />
    </div>
    <div class="flex-none flex message">
      <button :disabled="disabled" class="message-button" @click="showModal">
        <svg width="24" height="14" viewBox="0 0 24 14" xmlns="http://www.w3.org/2000/svg">
          <g stroke="#00A1D4" fill="none" fill-rule="evenodd" stroke-linejoin="round">
            <path d="M15.5 12.5c0 .55-.45 1-1 1h-13c-.55 0-1-.45-1-1v-11c0-.55.45-1 1-1h13c.55 0 1 .45 1 1v11zM23.5 12.5l-6-3v-5l6-3z"/>
          </g>
        </svg>
      </button>
      <textarea-autosize
        class="message-input"
        ref="messageInputTextarea"
        :disabled="disabled"
        :min-height="48"
        :max-height="350"
        rows="1"
        placeholder="Viesti..."
        v-model="content"
        @keydown.enter.exact.prevent
        @keyup.enter.exact="sendMessage"
      >
      </textarea-autosize>
      <button type="submit" class="message-button" :disabled="disabled"  @click="sendMessage">
        <svg width="21" height="21" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
          <g stroke="#00A1D4" fill="none" fill-rule="evenodd">
            <path d="M20 1L8.421 12.579"/>
            <path stroke-linecap="square" d="M20 1l-8.095 20-3.81-8.095L0 9.095z"/>
          </g>
        </svg>
      </button>

      <modal v-show="isModalVisible" @close="closeModal">
        <template slot="header">Aloitetaanko videokeskustelu asiakkaan kanssa?</template>
        <template slot="body">Asiakasta pyydetään avaamaan kännykästään videoyhteys. Asiakas ei näe sinua, ainoastaan kuulee äänesi.</template>
        <template slot="footer">
          <v-button type="button" @click="closeModal" class="mr2">Peruuta</v-button>
          <v-button type="button" @click="startVideoCall" variant="primary">Aloita videokeskustelu</v-button>
        </template>
      </modal>

      <modal
        v-show="isVideoModalVisible || videoToken"
        variant="video"
        :class="{fullscreen: isVideoFullscreen}"
        :backdrop="false"
      >
        <VideoCall
          v-if="isVideoModalVisible || videoToken"
          :chat="chat"
          :accessToken="videoToken"
          @stopCall="stopVideoCall"
          @toggleFullscreen="toggleVideoFullscreen"
        />
      </modal>
    </div>
    <div class="my1 right-align h6 gray-3"># = Näytä fraasit, Enter = Lähetä, Shift+Enter = Rivinvaihto</div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import Modal from '@/components/Modal.vue';
import Button from '@/components/Button.vue';
import Embed from '@/components/Embed.vue';
import TextareaAutosize from '@/components/TextareaAutosize.vue'
import PhraseList from '@/components/reception/PhraseList.vue'
import VideoCall from '@/components/reception/VideoCall.vue'

export default {
  components: {
    Modal,
    'v-button': Button,
    Embed,
    TextareaAutosize,
    PhraseList,
    VideoCall,
  },
  props: {
    chat: Object,
    messages: Array,
    disabled: Boolean,
  },
  data () {
    return {
      connected: false,
      isModalVisible: false,
      isVideoModalVisible: false,
      isVideoFullscreen: false,
      showPhrases: false,
    };
  },
  computed: {
    videoToken() {
      return this.videoCallTokenById[this.chat.chatId];
    },
    content: {
      get() {
        return this.chatInputById(this.chat.chatId);
      },
      set(value) {
        this.$store.commit('SET_CHAT_INPUT', {
          chatId: this.chat.chatId,
          input: value,
        });
        if(value.charAt(0) === '#') {
          this.$store.commit('SET_PHRASE_FILTER', value.substr(1));
          this.showPhrases = true;
        } else {
          this.showPhrases = false;
        }
      }
    },
    ...mapGetters({
      videoCallTokenById: 'videoCallTokenById',
      chatInputById: 'chatInputById',
    }),
  },
  methods: {
    sendMessage () {
      if (this.content && this.content.length > 0) {
        this.$store.dispatch('sendMessage', {
          chatId: this.chat.chatId,
          content: this.content,
        });
        this.content = '';
      }
    },
    showModal() {
      this.isModalVisible = true;
    },
    closeModal() {
      this.isModalVisible = false;
    },
    startVideoCall() {
      if (!this.videoToken) {
        this.$store.dispatch('startVideo', this.chat.chatId);
      }

      this.isVideoModalVisible = true;
      this.closeModal();
    },
    stopVideoCall() {
      if(this.videoToken) {
        this.$store.dispatch('stopVideo', this.chat.chatId);
      }

      this.isVideoModalVisible = false;
      this.closeModal();
    },
    toggleVideoFullscreen() {
      this.isVideoFullscreen = !this.isVideoFullscreen;
    },
    autofocus () {
      //i dont work at the moment because of input -> textarea-autosize change
      if (this.$refs.messageInputTextarea) {
        setTimeout(() => {
          this.$refs.messageInputTextarea.focus();
        }, 100);
      }
    }
  },
  watch: {
    videoToken: {
      handler(val, oldVal) {
        if (!!val !== !!oldVal) {
          this.isVideoModalVisible = !!val;
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
  .phrase-holder {
    max-height: 50vh;
    overflow-y: auto;
  }
  .phrase-holder::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 7px;
  }
  .phrase-holder::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: rgba(0,0,0,.5);
    -webkit-box-shadow: 0 0 1px rgba(255,255,255,.5);
  }

  .message {
    background-color: var(--gray);
    border-radius: var(--space-3);
    border-bottom-right-radius: 0;
  }

  .message-input {
    flex: auto;
    margin: 0;
    padding: .75rem;
    border: 0;
    font-size: var(--h4);
    resize: none;
    background-color: transparent;
  }

  .message-input:focus {
    outline: none;
  }

  .message-input::placeholder { color: #777 }
  .message-input:disabled { opacity: .5 }

  .message-button {
    padding-left: var(--space-2);
    padding-right: var(--space-2);
    border: 0;
    background-color: transparent;
    cursor: pointer;
  }

  .message-button:disabled {
    opacity: .5;
    cursor: default;
  }

  .message-button:active,
  .message-button:focus {
    outline: none;
  }

  .message-button:disabled svg g {
    stroke: var(--gray-3);
  }

  .message-button:first-child {
    border-right: 2px solid #fff;
  }

  .message-button svg {
    display: block;
  }
</style>
